import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { articles, channels, hotTags } from "@/mocks/articles";
import { formatViews } from "@/utils/format";

export default function TrendingSection() {
  const [mode, setMode] = useState<"day" | "week">("day");

  const dayList = useMemo(() => [...articles].sort((a, b) => b.views - a.views), []);
  const weekList = useMemo(
    () =>
      [...articles].sort(
        (a, b) => (b.date > a.date ? 1 : b.date < a.date ? -1 : b.views - a.views)
      ),
    []
  );
  const list = mode === "day" ? dayList : weekList;

  const featuredSel = dayList[1];
  const rankStyle = (i: number) => {
    if (i === 0) return "bg-primary-500 text-background-50";
    if (i === 1) return "bg-accent-500 text-background-50";
    if (i === 2) return "bg-secondary-500 text-background-50";
    return "bg-background-200 text-foreground-600";
  };

  return (
    <section id="trend" className="bg-background-100 border-b border-background-200 scroll-mt-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6 md:mb-8">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              今日热度榜单
            </h2>
            <p className="mt-1.5 text-sm text-foreground-500">
              按实时热度与讨论量排序，看看大家最近都在吃哪个瓜
            </p>
          </div>
          <div className="inline-flex items-center p-1 rounded-full bg-background-50 border border-background-200 self-start sm:self-auto">
            <button
              type="button"
              onClick={() => setMode("day")}
              className={`px-3.5 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors ${
                mode === "day" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              日榜
            </button>
            <button
              type="button"
              onClick={() => setMode("week")}
              className={`px-3.5 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors ${
                mode === "week" ? "bg-foreground-950 text-background-50" : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              周榜
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 榜单列表 */}
          <div className="lg:col-span-2 bg-background-50 rounded-xl border border-background-200 p-2 md:p-3">
            <ol className="divide-y divide-background-200/70">
              {list.slice(0, 8).map((a, i) => {
                const cat = channels.find((c) => c.id === a.category)!;
                return (
                  <li key={a.id}>
                    <Link
                      to={`/article/${a.id}`}
                      className="group flex items-center gap-3 md:gap-4 px-2 md:px-3 py-3 rounded-lg hover:bg-background-100 transition-colors"
                    >
                      <span className={`flex items-center justify-center w-7 h-7 md:w-8 md:h-8 shrink-0 rounded-md font-heading text-sm font-bold ${rankStyle(i)}`}>
                        {i + 1}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span className="block text-[15px] font-medium text-foreground-900 leading-snug line-clamp-1 group-hover:text-primary-700 transition-colors">
                          {a.title}
                        </span>
                        <span className="mt-0.5 flex items-center gap-2 text-xs text-foreground-500">
                          <span className="text-primary-600 font-medium">{cat.name}</span>
                          <span>{a.date}</span>
                        </span>
                      </span>
                      <span className="hidden sm:flex items-center gap-1.5 text-xs text-foreground-500 whitespace-nowrap">
                        <i className="ri-fire-line text-primary-500"></i>
                        {formatViews(a.views)} 热度
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>

          {/* 侧栏 */}
          <aside className="space-y-5">
            <div className="rounded-xl border border-accent-200 bg-accent-100/60 p-5">
              <p className="text-xs tracking-widest text-accent-800 font-medium">小编吃瓜笔记</p>
              <Link to={`/article/${featuredSel.id}`} className="mt-3 block">
                <h3 className="font-heading text-lg font-bold text-foreground-950 leading-snug hover:text-primary-700 transition-colors">
                  {featuredSel.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-foreground-600 line-clamp-3">{featuredSel.subtitle}</p>
                <span className="inline-flex items-center gap-1 mt-3 text-sm font-medium text-accent-800">
                  展开深挖
                  <i className="ri-arrow-right-line"></i>
                </span>
              </Link>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <p className="text-xs tracking-widest text-foreground-500 font-medium">本期热点关键词</p>
              <div className="mt-3 flex flex-wrap gap-2">
                {hotTags.map((t) => (
                  <span key={t} className="inline-flex items-center px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    <i className="ri-price-tag-3-line mr-1"></i>
                    {t}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-background-200 bg-background-50 p-5">
              <p className="text-xs tracking-widest text-foreground-500 font-medium">按栏目吃瓜</p>
              <div className="mt-3 space-y-2">
                {channels.map((c) => {
                  const count = articles.filter((a) => a.category === c.id).length;
                  return (
                    <Link
                      key={c.id}
                      to={`/channel/${c.id}`}
                      className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 hover:bg-accent-100 transition-colors group"
                    >
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-accent-900">{c.name}</span>
                      <span className="text-xs text-foreground-500 flex items-center gap-1">
                        {count} 篇
                        <i className="ri-arrow-right-s-line group-hover:translate-x-0.5 transition-transform"></i>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}